import React, { useCallback } from 'react';
import { TextField } from 'src/components/basic';
import SVGIcon, { BASE_ICONS } from 'src/components/svg';
import { TableLibrary } from 'src/components/complex';
import { remCss, rgba, scssVariables as variables, scssVariables } from 'src/helpers';

export const EditCell = ({
  valid,
  onMove,
  onFocus,
  onPaste,
  onEnter,
  value,
  onChange,
  ...props
}) => {
  const [, setFocused] = React.useState(false);
  const [paste, setPaste] = React.useState(null);

  return (
    <TextField
      {...props}
      onChange={(n, v, e) => {
        if (paste) {
          setPaste(false);
          onPaste(n, v.split(/[,;\t]+/)[0] + paste.substring(paste.split(/[,;\t]+/)[0].length), e);
        } else {
          onChange(n, v, e);
        }
      }}
      template={7}
      value={value || ''}
      inputProps={{
        autoComplete: 'off',
        onKeyUp: (e) => {
          if (e.key === 'Enter' || e.code === 'Enter') {
            onEnter(e);
            return false;
          }
        },
        onKeyDown: (e) => {
          if (
            e.key === 'ArrowUp' ||
            e.key === 'ArrowDown' ||
            e.key === 'ArrowLeft' ||
            e.key === 'ArrowRight'
          ) {
            onMove(e, e.key.replace('Arrow', '').toLowerCase());
          }
        },
        onPaste: (e) => {
          setPaste(e.clipboardData.getData('text/plain'));
        },
        onFocus: (e) => {
          setFocused(true);
          onFocus(e);
        },
        onBlur: () => {
          setFocused(false);
        }
      }}
      suffixEl={
        !!valid && !!value && valid(value) ? (
          <div className={'mc-mr10 mc-flex mc-flex-center'}>
            <SVGIcon
              outline
              circle
              padding={remCss(5)}
              iconname={BASE_ICONS.CHECK}
              fill={variables['colors-mc-primary']}
              height={remCss(25)}
              width={remCss(25)}
            />
          </div>
        ) : (
          ''
        )
      }
    />
  );
};

export const EDITABLE_TABLE_ROWS_LIMIT = 100;
const MCEditableTable = ({ list, setList, columns, onHandleChangeValid }) => {
  const handleChange = useCallback(
    (item, index, n, v) => {
      if (onHandleChangeValid) onHandleChangeValid(item, index, n, v);
      setList(list.map((item, i) => (i === index ? { ...item, [n]: v } : item)));
    },
    [list, onHandleChangeValid, setList]
  );

  const addNew = useCallback(() => {
    const addNumber = Math.min(EDITABLE_TABLE_ROWS_LIMIT - list.length, 4);
    if (addNumber > 0) setList([...list, ...Array.from({ length: addNumber }, () => ({}))]);
  }, [list, setList]);

  const onFocus = useCallback(
    (e, rowIndex) => {
      const nextRow = rowIndex + 2;
      if (list.length < nextRow) {
        addNew();
      }
    },
    [addNew, list]
  );
  const onMove = (e, rowIndex, colIndex, move) => {
    let isNext = true;
    switch (move) {
      case 'up':
        rowIndex--;
        break;
      case 'down':
        rowIndex++;
        break;
      case 'left':
        colIndex--;
        isNext = false;
        break;
      case 'right':
        colIndex++;
        break;
    }
    handleMove(rowIndex + 1, colIndex + 1, isNext);
  };

  const handleMove = (row, col, isNext = true) => {
    if (list.length < row) {
      if (list.length < EDITABLE_TABLE_ROWS_LIMIT) {
        addNew();
        setTimeout(() => {
          handleMove(row, col);
        }, 200);
      }
    } else {
      const el = document.querySelector(`tbody tr:nth-child(${row}) td:nth-child(${col}) input`);
      if (el) {
        el.scrollIntoView({ block: 'center', behavior: 'smooth' });
        setTimeout(() => {
          el.focus();
        }, 100);
      } else if (isNext) handleMove(row + 1, columns.findIndex((c) => !!c.renderEditCell) + 1);
      else handleMove(row - 1, columns.findLastIndex((c) => !!c.renderEditCell) + 1);
    }
  };

  const onPaste = useCallback(
    (n, v, event, rowIndex, colIndex) => {
      const pastedText = v;

      if (pastedText) {
        const lines = pastedText.split(/[\n\r]+/);

        const oldList = [...list];

        lines.forEach((line, lineIndex) => {
          let cells = line.split(/[,;\t]+/);
          let editableColumns = columns.filter((c) => !!c.renderEditCell);
          let startColumnIndex =
            lineIndex === 0
              ? editableColumns.findIndex((c) => c.name === columns[colIndex].name)
              : 0;
          let data = oldList[rowIndex + lineIndex] || {};
          cells = [
            ...cells,
            ...Array.from(
              { length: editableColumns.length - startColumnIndex - cells.length },
              () => null
            )
          ];

          cells.forEach((cell, cellIndex) => {
            const col = editableColumns[cellIndex + startColumnIndex];
            if (col) {
              data[col.name] = cell;
              if (onHandleChangeValid)
                onHandleChangeValid(data, rowIndex + lineIndex, col.name, data[col.name]);
            }
            oldList[rowIndex + lineIndex] = data;
          });
        });
        setList(oldList);
      }
    },
    [columns, list, onHandleChangeValid, setList]
  );

  const convertColumns = (columns) => {
    return columns.map(({ renderEditCell, name, ...col }, colIndex) => {
      if (!!renderEditCell && !name) {
        throw Error('Edit Cell need name property');
      }
      return {
        ...col,
        renderCell: (item, rowIndex, ...a) => {
          return renderEditCell
            ? renderEditCell(
                {
                  onEnter: (e) => onMove(e, rowIndex, colIndex, 'down'),
                  onFocus: (e) => onFocus(e, rowIndex),
                  onPaste: (n, v, e) => onPaste(n, v, e, rowIndex, colIndex),
                  onMove: (e, move) => onMove(e, rowIndex, colIndex, move),
                  value: item[name],
                  name: name,
                  onChange: (n, v) => {
                    handleChange(item, rowIndex, n, v);
                  },
                  template: 6
                },
                item,
                rowIndex,
                ...a
              )
            : col.renderCell(item, rowIndex, ...a);
        }
      };
    });
  };

  return (
    <TableLibrary
      template={4}
      otherProps={{
        row: (item, index) => ({
          style:
            item.ok === false
              ? {
                  backgroundColor: rgba(
                    scssVariables['colors-mc-yellow'],
                    index % 2 === 0 ? 0.2 : 0.1
                  )
                }
              : {}
        }),
        header: (col) => ({
          style: { ...col.cellStyles, height: remCss(40) }
        })
      }}
      columns={convertColumns(columns)}
      data={list}
      haveFooter={false}
    />
  );
};

MCEditableTable.displayName = 'MCEditableTable';

export default MCEditableTable;
